import { HStack } from '@/components/ui/hstack';
import { Center } from '@/components/ui/center';
import { Pressable } from '@/components/ui/pressable';
import { Box } from '@/components/ui/box';
import React, { useEffect, useState, useRef } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useSelector } from 'react-redux';
import i18n from '../../assets/i18n';
import Media from './Media';
import * as MediaSelector from './MediaSelector';
import empty from '../../assets/images/placeholder.png';
import { IconM } from './IconsV2';

export default function MediaCarousell({ uris, overlays, isEdit, update }) {
    isEdit = isEdit == undefined ? false : isEdit;
    const pageWidth = Platform.OS == 'web' ? 390 : useWindowDimensions().width;
    const [size, setSize] = useState(Math.min(480, pageWidth));
    const [page, setPage] = useState(0);
    const [actUris, setActUris] = useState(isEdit ? [...uris, empty] : uris);
    const imgLoad = useSelector((state) => state.images.load);
    const imageCrop = useSelector((state) => state.settings.imageCrop);

    const scrollRef = useRef();

    // component mounted
    useEffect(() => {
        MediaSelector.requestPermissions();
        if (update) {
            MediaSelector.ensureDirExists();
        }
    }, []);

    // local state propagated to parent component
    useEffect(() => {
        async function storeMedia() {
            const newUris = await MediaSelector.storeImagesAsync(actUris);
            update(newUris.filter((uri) => uri != empty));
        }
        if (update) {
            storeMedia();
        }
        if (!isEdit) {
            setPage(0);
            scrollRef.current?.scrollTo({ x: 0, y: 0, animated: false });
        }
    }, [actUris]);

    // changed property triggers update of local state
    useEffect(() => {
        if (!isEdit) {
            // console.log("new uris", uris)
            setActUris(uris);
        }
    }, [uris]);

    function updateUris(image) {
        if (null != image) {
            setActUris((oldUris) => {
                let newUris = [...oldUris];
                newUris[page] = image.uri;
                if (page == newUris.length - 1) {
                    // add new placeholder
                    newUris.push(empty);
                }
                return newUris;
            });
        }
    }

    const takePhoto = () => {
        MediaSelector.takePhoto(imageCrop, updateUris);
    };

    const takeVideo = () => {
        MediaSelector.takeVideo(imageCrop, updateUris);
    };

    const pickImage = () => {
        MediaSelector.pickImage(i18n.t('mediaManager.imageLoadTitle'), imageCrop, updateUris);
    };

    const deleteImage = () => {
        if (actUris[page] != empty) {
            setActUris((oldUris) => [...oldUris.slice(0, page), ...oldUris.slice(page + 1)]);
        }
    };

    function handleScroll(event) {
        let actPage = Math.round(event.nativeEvent.contentOffset.x / pageWidth);
        if (actPage != page) {
            setPage(actPage);
        }
    }

    return (
        <ScrollView>
            <ScrollView ref={scrollRef} horizontal={true} showsHorizontalScrollIndicator={actUris.length > 1} decelerationRate={0} snapToInterval={Platform.OS == 'web' ? 390 : pageWidth} height={size} snapToAlignment="center" onScroll={handleScroll} scrollEventThrottle={256}>
                {actUris.map((uri, u) => (
                    <Media key={uri + '_' + imgLoad[uri]?.duration} uri={uri} overlay={overlays?.[uri]} width={pageWidth} size={size} active={u == page} />
                ))}
            </ScrollView>
            {isEdit && (
                <HStack space="2xl" className={` bottom-1/3 absolute self-center `}>
                    <Box className="rounded-full border-[10px] border-primary-900 bg-primary-900">
                        <IconM className="text-3xl text-secondary-50" name="videocam" onPress={takeVideo} />
                    </Box>
                    <Box className="rounded-full border-[10px] border-primary-900 bg-primary-900">
                        <IconM className="text-3xl text-secondary-50" name="photo-camera" onPress={takePhoto} />
                    </Box>
                    <Box className="rounded-full border-[10px] border-primary-900 bg-primary-900">
                        <IconM className="text-3xl text-secondary-50" name="photo-library" onPress={pickImage} />
                    </Box>
                    {actUris[page] != empty && (
                        <Box className="rounded-full border-[10px] border-primary-900 bg-primary-900">
                            <IconM className="text-3xl text-secondary-50" name="delete" onPress={deleteImage} />
                        </Box>
                    )}
                </HStack>
            )}
            <Center className="flex-1">
                {actUris.length > 1 && (
                    <HStack space="xs" className="absolute bottom-5">
                        {actUris.map((uri, u) => (
                            <Pressable key={'U' + u} className={` ${page == u ? 'bg-primary-900' : 'bg-secondary-50'} p-1 rounded-full border-[2px] border-primary-900 `} />
                        ))}
                    </HStack>
                )}
            </Center>
        </ScrollView>
    );
}
